/* Basic Apparel custom: split size selector, sticky add to cart, mobile thumbnails, color picker, read more. */
(() => {
  /* True when every option has a checked input (form validation would fire "invalid" events). */
  const allOptionsChosen = (form) => {
    const inputs = [...form.querySelectorAll('input[data-option]')];
    const options = new Set(inputs.map((input) => input.dataset.option));
    const chosen = new Set(inputs.filter((input) => input.checked).map((input) => input.dataset.option));
    return options.size === chosen.size;
  };

  /* Split selector: "M / L30" shown as two rows. Picks the matching radio input, which drives the theme's variant logic. */
  const initSplit = (split) => {
    const product = split.closest('[data-component="product"]');
    const data = JSON.parse(product.querySelector('[data-data]').textContent);
    const optionIndex = Number(split.dataset.optionIndex);
    const inputs = [...product.querySelectorAll(`input[data-option="${optionIndex}"]`)];
    const buttons = [...split.querySelectorAll('[data-ba-part]')];
    const valueLabels = [...split.querySelectorAll('[data-ba-split-value]')];

    const checked = inputs.find((input) => input.checked);
    const selected = checked ? checked.value.split(' / ').map((part) => part.trim()) : ['', ''];

    const isAvailable = (partIndex, value) => {
      const other = selected[1 - partIndex];
      return data.variants.some((variant) => {
        if (!variant.available) return false;
        const parts = variant.options[optionIndex].split(' / ').map((part) => part.trim());
        if (parts[partIndex] !== value) return false;
        return other === '' || parts[1 - partIndex] === other;
      });
    };

    const render = () => {
      buttons.forEach((button) => {
        const partIndex = Number(button.dataset.baPart);
        const isSelected = selected[partIndex] === button.dataset.value;
        button.classList.toggle('is-selected', isSelected);
        button.setAttribute('aria-pressed', String(isSelected));
        button.classList.toggle('option-label--unavailable', !isAvailable(partIndex, button.dataset.value));
      });
      valueLabels.forEach((label, index) => {
        label.textContent = selected[index];
      });
    };

    const commit = () => {
      if (selected[0] === '' || selected[1] === '') return;
      const input = inputs.find((item) => item.value === `${selected[0]} / ${selected[1]}`);
      if (input) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }
      /* Combination does not exist: clear the theme's selection so the button shows "unavailable". */
      inputs.forEach((item) => {
        item.checked = false;
      });
      const select = product.querySelector(`#product-${data.id}-option-${optionIndex}`);
      select.value = '';
      select.dispatchEvent(new Event('change'));
    };

    buttons.forEach((button) => {
      button.addEventListener('click', () => {
        selected[Number(button.dataset.baPart)] = button.dataset.value;
        split.classList.remove('is-invalid');
        render();
        commit();
      });
    });

    render();
  };

  /* Sticky add to cart: visible while the main button is off screen. */
  const initStickyAtc = (bar) => {
    const product = document.querySelector('[data-component="product"]');
    const form = product.querySelector('form.product__form');
    const mainButton = form.querySelector('[data-add-to-cart]');
    const stickyButton = bar.querySelector('[data-ba-sticky-button]');
    const price = product.querySelector('[data-price]');
    const stickyPrice = bar.querySelector('[data-ba-sticky-price]');
    const stickyVariant = bar.querySelector('[data-ba-sticky-variant]');
    const variantSelect = form.querySelector('select[name="id"]');

    const sync = () => {
      stickyButton.textContent = mainButton.textContent.trim();
      stickyButton.disabled = mainButton.disabled;
      stickyPrice.textContent = price.textContent.trim();
      stickyVariant.textContent = allOptionsChosen(form) && variantSelect.selectedOptions[0]
        ? variantSelect.selectedOptions[0].textContent.split(' - ')[0].trim()
        : '';
    };

    new MutationObserver(sync).observe(mainButton, { childList: true, characterData: true, subtree: true, attributes: true });
    new MutationObserver(sync).observe(price, { childList: true, characterData: true, subtree: true });
    form.addEventListener('change', () => requestAnimationFrame(sync));
    sync();

    new IntersectionObserver(([entry]) => {
      const visible = !entry.isIntersecting;
      bar.classList.toggle('is-visible', visible);
      bar.setAttribute('aria-hidden', String(!visible));
      stickyButton.tabIndex = visible ? 0 : -1;
      document.documentElement.classList.toggle('ba-sticky-atc-visible', visible);
    }).observe(mainButton);

    stickyButton.addEventListener('click', () => {
      if (allOptionsChosen(form)) {
        mainButton.click();
        return;
      }
      const target = form.querySelector('[data-ba-split]') || form.querySelector('.product__form .space-y-2\\.5');
      if (target) {
        target.classList.add('is-invalid');
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });
  };

  /* Show a hint instead of the browser's validation bubble when no size is chosen on a split selector. */
  const initSplitValidation = (form) => {
    form.addEventListener('invalid', (event) => {
      const split = form.querySelector('[data-ba-split]');
      if (!split) return;
      event.preventDefault();
      split.classList.add('is-invalid');
      split.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, true);
  };

  /* Mobile thumbnails: drive the Splide slider through its pagination buttons. */
  const initThumbs = (thumbs) => {
    const media = thumbs.closest('[data-component="product-media"]');
    const thumbButtons = [...thumbs.querySelectorAll('[data-ba-thumb]')];

    const setActive = (index) => {
      thumbButtons.forEach((button, i) => {
        button.classList.toggle('is-active', i === index);
        if (i === index) {
          button.setAttribute('aria-current', 'true');
          thumbs.scrollTo({ left: button.offsetLeft - thumbs.clientWidth / 2 + button.clientWidth / 2, behavior: 'smooth' });
        } else {
          button.removeAttribute('aria-current');
        }
      });
    };

    thumbButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const page = media.querySelectorAll('.splide__pagination__page')[Number(button.dataset.baThumb)];
        if (page) page.click();
      });
    });

    const slides = [...media.querySelectorAll('.splide__slide')];
    const observer = new MutationObserver(() => {
      const index = slides.findIndex((slide) => slide.classList.contains('is-active'));
      if (index > -1) setActive(index);
    });
    slides.forEach((slide) => observer.observe(slide, { attributes: true, attributeFilter: ['class'] }));
  };

  /* Color picker: show all / fewer, preview the color name on hover. */
  const initColors = (picker) => {
    const toggle = picker.querySelector('[data-ba-colors-toggle]');
    const name = picker.querySelector('[data-ba-color-name]');
    const currentName = name.textContent;

    picker.querySelectorAll('[data-color-name]').forEach((link) => {
      link.addEventListener('mouseenter', () => {
        name.textContent = link.dataset.colorName;
      });
      link.addEventListener('mouseleave', () => {
        name.textContent = currentName;
      });
    });

    if (!toggle) return;
    toggle.addEventListener('click', () => {
      const open = picker.classList.toggle('is-open');
      toggle.setAttribute('aria-expanded', String(open));
      toggle.querySelector('[data-more]').hidden = open;
      toggle.querySelector('[data-less]').hidden = !open;
    });
  };

  /* Read more on clamped text. */
  const initClamp = (toggle) => {
    const text = toggle.previousElementSibling;
    const inner = text.firstElementChild;
    if (inner.scrollHeight <= inner.clientHeight + 2) return;
    toggle.hidden = false;
    toggle.addEventListener('click', () => {
      const open = text.classList.toggle('is-open');
      toggle.querySelector('[data-more]').hidden = open;
      toggle.querySelector('[data-less]').hidden = !open;
    });
  };

  const init = (root) => {
    root.querySelectorAll('[data-ba-split]').forEach(initSplit);
    root.querySelectorAll('[data-component="product"] form.product__form').forEach(initSplitValidation);
    root.querySelectorAll('[data-ba-sticky-atc]').forEach(initStickyAtc);
    root.querySelectorAll('[data-ba-thumbs]').forEach(initThumbs);
    root.querySelectorAll('[data-ba-colors]').forEach(initColors);
    root.querySelectorAll('[data-ba-clamp-toggle]').forEach(initClamp);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document));
  } else {
    init(document);
  }
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
